import { Alert } from '@mui/material';
import { useMutation, useQuery, useSubscription } from '@apollo/client';
import React, { useEffect, useState, useCallback, useContext, useRef } from 'react';
import { isEmpty } from 'lodash';
import { GET_ACTIVE_SENT_REFERRALS } from '../gql';
import ActiveSentReferralsTable from '../tables/ActiveSentReferralsTable';
import { usePromoteReferral } from '../../referral/redux/promoteReferral';
import Swal from 'sweetalert2';
import Referral from '../../referral/models/Referral';
import clientGraphql from '../../../common/apollo-graphql';
import AuthContext from '../../auth/context/AuthContext';
import { DELETE_REFERRAL_MUTATION } from '../../referral/gql/Mutation';
import { REFERRAL_REFRESH_SUBSCRIPTION } from '../../referral/gql/Subscription';
import ReferralCompleteDialog from '../dialogs/ReferralCompleteDialog';
import DialogReferralNotes from '../dialogs/DialogReferralNotes';
import moment from 'moment';
import SetAppointmentReferralDialog from '../dialogs/SetAppointmentReferralDialog';
import UpdateMiniReferralDialog from '../dialogs/UpdateMiniReferralDialog';
import GlobalLoader from '../../common/components/loader/GlobalLoader';

export default function ActiveSentReferrals({ history, uuid }) {
  const currentUser = useContext(AuthContext);

  // ---------------------------
  // STATE
  // ---------------------------
  const [parsedData, setParsedData] = useState({ rows: [], count: 0 });
  const [searchFilters, setSearchFilters] = useState({
    type: 'Name',
    patientName: '',
    birthdate: '',
    filterFax: 'all',
  });

  const [referralUuid, setReferralUuid] = useState('');
  const [referral, setReferral] = useState(null);
  const [openNotes, setOpenNotes] = useState(false);
  const [openReferralDialog, setOpenReferralDialog] = useState(false);
  const [status, setStatus] = useState('');
  const [appointmentDate, setAppointmentDate] = useState('');
  const [scheduleAppointmentDate, setScheduleAppointmentDate] = useState('');
  const [openCompleteDialog, setOpenCompleteDialog] = useState(false);
  const [openAppointmentDialog, setOpenAppointmentDialog] = useState(false);
  const [closeError, setCloseError] = useState(false);

  const subscriptionsRef = useRef([]);

  let refetchData;

  // ---------------------------
  // MUTATIONS / QUERIES
  // ---------------------------
  const [deleteReferral] = useMutation(DELETE_REFERRAL_MUTATION, { client: clientGraphql });

  const { loading, error, data, refetch, networkStatus } = useQuery(GET_ACTIVE_SENT_REFERRALS, {
    client: clientGraphql,
    variables: {
      uuid,
      patientName: null,
      birthdate: null,
      isFax: null,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !uuid || Object.keys(uuid).length === 0,
    notifyOnNetworkStatusChange: true,
  });

  const { data: subscriptionData } = useSubscription(REFERRAL_REFRESH_SUBSCRIPTION, {
    variables: { clinic_id: currentUser?.clinics?.[0]?.id || null },
  });

  const { promoteReferral } = usePromoteReferral();

  // ---------------------------
  // ACTIONS
  // ---------------------------
  const handleOnDelete = useCallback((rowId) => {
    Swal.fire({
      title: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
    }).then((result) => {
      if (result.isConfirmed) {
        deleteReferral({ variables: { referralUuid: rowId } }).then(() => refetchData(searchFilters));
      }
    });
  }, [deleteReferral, refetchData, searchFilters]);

  const handleOnComplete = useCallback((item) => {
    setReferralUuid(item.id);
    setAppointmentDate(item?.appointment?.appointment_date ? moment(item.appointment.appointment_date.replace('T', ' ')).format('DD MMM YYYY - HH:mm:ss') : '');
    if (item.status !== 'declined') {
      setOpenCompleteDialog(true);
    } else {
      promoteReferral(item.id, 'ref_completed', 'Referral Completed').then(() => refetchData(searchFilters));
    }
  }, [promoteReferral, searchFilters, refetchData]);

  const handleSetAppointment = useCallback((item) => {
    setScheduleAppointmentDate(item?.appointment?.appointment_date);
    setReferralUuid(item.id);
    setStatus(item.status);
    setOpenAppointmentDialog(true);
  }, []);

  const onPromoteReferral = useCallback((id, data) => {
    promoteReferral(id, 'ref_completed', data)
      .then(() => {
        const tab = data === 'Referral Completed' ? 'Declined' : 'Referral History';
        Swal.fire({ icon: 'success', text: `This referral has been moved to your ${tab} tab`, showConfirmButton: true })
          .then(() => refetchData(searchFilters));
      })
      .catch(() => Swal.fire({ icon: 'error', text: 'Failed to complete referral!', showConfirmButton: true }));
  }, [promoteReferral, refetchData, searchFilters]);

  const getReferralActions = useCallback((item) => ({
    onViewNotes: () => { setReferralUuid(item.id); setOpenNotes(true); },
    onUpdate: () => history.push(`/referrals/${item.id}?edit=1`),
    onViewReferral: () => history.push(`/referrals/${item.id}/review`),
    onUpdateReferral: () => { setReferral(item); setOpenReferralDialog(true); },
    onComplete: () => handleOnComplete(item),
    onSetAppointment: () => handleSetAppointment(item),
    onDelete: () => handleOnDelete(item.id),
  }), [history, handleOnComplete, handleOnDelete, handleSetAppointment]);

  // ---------------------------
  // DEBOUNCE REFETCH
  // ---------------------------
  const debounceRef = useRef(null);

  refetchData = useCallback((filters) => {
    if (!refetch) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      refetch({
        uuid,
        patientName: filters.patientName || null,
        birthdate: filters.birthdate || null,
        isFax: filters.filterFax === 'fax' ? true : filters.filterFax === 'none' ? false : null,
      }).then(({ data }) => {
        if (!isEmpty(data) && !isEmpty(data.rows)) {
          setParsedData({
            rows: data.rows.map((item) => ({
              ...item,
              user: currentUser,
              referral: new Referral(item),
              actions: getReferralActions(item),
            })),
            count: data.rows.length,
          });
        } else {
          setParsedData({ rows: [], count: 0 });
        }
      });
    }, 400);
  }, [refetch, currentUser, uuid, getReferralActions]);

  // ---------------------------
  // FILTER HANDLERS
  // ---------------------------
  const handleFilterChange = (e) => {
    const value = e.target.value;
    const newFilters = { ...searchFilters };
    if (searchFilters.type === 'Name') newFilters.patientName = value;
    else newFilters.birthdate = value;

    setSearchFilters(newFilters);
    refetchData(newFilters);
  };

  const handleFilterFaxChange = (e) => {
    const newFilters = { ...searchFilters, filterFax: e.target.value };
    setSearchFilters(newFilters);
    refetchData(newFilters);
  };

  const handleTypeChange = (e) => {
    const newFilters = { type: e.target.value, patientName: '', birthdate: '', filterFax: searchFilters.filterFax };
    setSearchFilters(newFilters);
    refetchData(newFilters);
  };

  // ---------------------------
  // INITIAL DATA / SUBSCRIPTION
  // ---------------------------
  useEffect(() => {
    if (!isEmpty(data) && !isEmpty(data.rows)) {
      setParsedData({
        rows: data.rows.map((item) => ({
          ...item,
          user: currentUser,
          referral: new Referral(item),
          actions: getReferralActions(item),
        })),
        count: data.rows.length,
      });
    }
  }, [data, currentUser, getReferralActions]);

  useEffect(() => {
    if (!isEmpty(subscriptionData)) {
      if (!isEmpty(subscriptionsRef.current) && JSON.stringify(subscriptionData) !== JSON.stringify(subscriptionsRef.current)) {
        refetchData(searchFilters);
      }
      subscriptionsRef.current = subscriptionData;
    }
  }, [subscriptionData, refetchData, searchFilters]);

  if (networkStatus === 1) return <GlobalLoader />;

  return (
    <>
      {error && !closeError && (
        <Alert variant="filled" severity="error" onClose={() => setCloseError(true)}>
          We are sorry, but we are having difficulty with our system. We are unable to load data at
          the moment. Please reach out to us if you continue to experience this issue.
        </Alert>
      )}

      <ActiveSentReferralsTable
        onFilterChanged={handleFilterChange}
        onFilterFaxChanged={handleFilterFaxChange}
        onChangeDropdown={handleTypeChange}
        loading={loading}
        refresh={() => refetchData(searchFilters)}
        data={!isEmpty(parsedData) ? parsedData : { rows: [], count: 0 }}
      />

      <DialogReferralNotes
        referralUuid={referralUuid}
        openNotes={openNotes}
        handleClose={() => setOpenNotes(false)}
        isExternal={false}
      />

      <ReferralCompleteDialog
        initialAppointmentDate={appointmentDate}
        initialOpen={openCompleteDialog}
        referralUuid={referralUuid}
        onPromoteReferral={onPromoteReferral}
        onClose={() => setOpenCompleteDialog(false)}
      />

      <UpdateMiniReferralDialog
        open={openReferralDialog}
        onClose={() => { setOpenReferralDialog(false); refetchData(searchFilters); }}
        referral={referral}
        isPublicPatient
        isReceived={false}
      />

      <SetAppointmentReferralDialog
        onOpen={openAppointmentDialog}
        onClose={() => { setOpenAppointmentDialog(false); refetchData(searchFilters); }}
        history={history}
        uuid={referralUuid}
        status={status}
        appointmentDate={scheduleAppointmentDate}
        reschedule={Boolean(scheduleAppointmentDate)}
      />
    </>
  );
}
